import { useRef, useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Coins, Lock, MapPin, Pencil, Plus, RotateCcw, Trash2, TrendingUp } from 'lucide-react';
import { api, type CalendarSettings } from '../lib/api';
import { cn, formatDate, formatMoney } from '../lib/utils';
import { BsnField } from '../components/onboarding/BsnField';
import { AfasSection } from '../components/AfasSection';
import { AutomatischSection } from '../components/AutomatischSection';
import { Card, Disclosure, EmptyState, ICON, IconButton, Kpi, Notice, PageHeader, Sheet, Skeleton, Tabs } from '../components/ui';
import {
  isValidBsn,
  isValidDutchPhone,
  isValidDutchPostcode,
  isValidIban,
  normalizeDutchPostcode,
} from '../components/onboarding/shared';

type SectionKey = 'profile' | 'trigion' | 'objects' | 'rooster' | 'calendar' | 'afas' | 'automatisch' | 'notifications';

const SECTIONS: Array<{ value: SectionKey; label: string }> = [
  { value: 'profile', label: 'Profiel' },
  { value: 'trigion', label: 'Werkgever' },
  { value: 'objects', label: 'Objecten' },
  { value: 'rooster', label: 'Rooster' },
  { value: 'calendar', label: 'Agenda' },
  { value: 'afas', label: 'AFAS' },
  { value: 'automatisch', label: 'Automatisch' },
];

export function SettingsPage() {
  const [section, setSection] = useState<SectionKey>(() => {
    const h = window.location.hash.slice(1);
    return h === 'afas' || h === 'automatisch' ? h : 'profile';
  });
  return (
    <div className="page">
      <PageHeader title="Instellingen" />
      <Tabs items={SECTIONS} value={section} onChange={setSection} />

      {section === 'profile' && <ProfileSection />}
      {section === 'trigion' && <TrigionSection />}
      {section === 'objects' && <ObjectsSection />}
      {section === 'rooster' && <RoosterSyncSection />}
      {section === 'calendar' && <CalendarSection />}
      {section === 'notifications' && <NotificationsSection />}
      {section === 'afas' && <AfasSection />}
      {section === 'automatisch' && <AutomatischSection />}
    </div>
  );
}

// --- Kleine bouwstenen --------------------------------------------------------

/** Formulierveld: label, invoer, en daaronder een fout of een korte hint. */
function Field({
  label,
  htmlFor,
  error,
  hint,
  required,
  wide,
  children,
}: {
  label: ReactNode;
  htmlFor?: string;
  error?: string;
  hint?: ReactNode;
  required?: boolean;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={cn('min-w-0', wide && 'sm:col-span-2')}>
      <label className="label" htmlFor={htmlFor}>
        {label}
        {required && <span className="text-red-300"> *</span>}
      </label>
      {children}
      {error ? (
        <p className="mt-1.5 text-xs text-red-300">{error}</p>
      ) : hint ? (
        <p className="mt-1.5 text-xs text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}

function FieldGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>;
}

function InfoList({ children }: { children: ReactNode }) {
  return <dl className="divide-y divide-surface-line/60 text-sm">{children}</dl>;
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2.5">
      <dt className="shrink-0 text-slate-400">{label}</dt>
      <dd className="min-w-0 break-words text-right text-slate-100">{children}</dd>
    </div>
  );
}

type SaveStatus = { tone: 'ok' | 'err'; text: string } | null;

/** Onderrand van een formulierkaart: melding links, Opslaan rechtsonder. */
function SaveBar({ status, pending, onClick }: { status: SaveStatus; pending: boolean; onClick?: () => void }) {
  return (
    <div className="mt-4 flex flex-wrap items-center justify-end gap-3 border-t border-surface-line/60 pt-4">
      {status && (
        <div className="mr-auto min-w-0">
          <Notice tone={status.tone}>{status.text}</Notice>
        </div>
      )}
      <button type="submit" className="btn-secondary" disabled={pending} onClick={onClick}>
        {pending ? 'Opslaan…' : 'Opslaan'}
      </button>
    </div>
  );
}

function CardSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="card space-y-3" aria-busy="true" aria-label="Laden">
      <Skeleton className="h-4 w-32" />
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  );
}

/**
 * Eén profiel-PATCH voor meerdere kaarten: elke kaart heeft een eigen Opslaan-knop,
 * de melding verschijnt in de kaart waarop je klikte.
 */
function useProfileSave() {
  const qc = useQueryClient();
  const cardRef = useRef<string>('');
  const [status, setStatus] = useState<{ card: string; tone: 'ok' | 'err'; text: string } | null>(null);
  const mut = useMutation({
    mutationFn: (data: any) => api.profile.patch(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['profile'] });
      const card = cardRef.current;
      setStatus({ card, tone: 'ok', text: 'Opgeslagen' });
      setTimeout(() => setStatus((s) => (s?.card === card && s.tone === 'ok' ? null : s)), 2500);
    },
    onError: (err: Error) => setStatus({ card: cardRef.current, tone: 'err', text: err.message }),
  });
  const fail = (text: string) => setStatus({ card: cardRef.current, tone: 'err', text });
  const bar = (card: string) => (
    <SaveBar
      status={status?.card === card ? { tone: status.tone, text: status.text } : null}
      pending={mut.isPending}
      onClick={() => {
        cardRef.current = card;
      }}
    />
  );
  return { mut, fail, bar, clear: () => setStatus(null) };
}

// --- Profiel ------------------------------------------------------------------

function ProfileSection() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: profile } = useQuery({ queryKey: ['profile'], queryFn: api.profile.get });
  const [form, setForm] = useState<any | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useProfileSave();

  const resetMut = useMutation({
    mutationFn: () => api.onboarding.reset(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['onboarding'] });
      navigate('/onboarding');
    },
  });

  if (!profile) return <CardSkeleton rows={6} />;

  const f: any = form ?? profile;
  const set = (k: string, v: any) => setForm({ ...(form ?? profile), [k]: v });

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (f.bsn && !isValidBsn(f.bsn)) e.bsn = 'Dit BSN-nummer voldoet niet aan de elfproef';
    if (f.phone && !isValidDutchPhone(f.phone)) e.phone = 'Vul een geldig Nederlands telefoonnummer in';
    if (f.address_postcode && !isValidDutchPostcode(f.address_postcode)) e.address_postcode = 'Gebruik het formaat 1234 AB';
    if (f.iban && !isValidIban(f.iban)) e.iban = 'Dit IBAN klopt niet';
    if (!f.voornaam?.trim()) e.voornaam = 'Voornaam is verplicht';
    if (!f.achternaam?.trim()) e.achternaam = 'Achternaam is verplicht';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      save.fail('Controleer de gemarkeerde velden.');
      return;
    }
    save.clear();
    const payload: any = { ...f };
    if (payload.address_postcode) payload.address_postcode = normalizeDutchPostcode(payload.address_postcode);
    if (payload.iban) payload.iban = String(payload.iban).replace(/\s+/g, '').toUpperCase();
    // Strip read-only fields that the patch schema doesn't accept
    delete payload.email;
    delete payload.google_sub;
    delete payload.created_at;
    delete payload.updated_at;
    delete payload.id;
    delete payload.theme;
    delete payload.locale;
    delete payload.week_start;
    delete payload.svpb_aangemeld;
    delete payload.svpb_id_uploaded;
    delete payload.screening_aangevraagd;
    delete payload.screening_in;
    delete payload.screening_uit;
    delete payload.vgb_ontvangen;
    delete payload.pok_ontvangen;
    delete payload.pok_getekend;
    delete payload.dienstsoort;
    save.mut.mutate(payload);
  };

  const err = (k: string) => (errors[k] ? 'border-red-500' : undefined);

  return (
    <>
      <form className="page" onSubmit={submit} noValidate>
        <Card title="Naam">
          <FieldGrid>
            <Field label="Voornaam" htmlFor="p-voornaam" required error={errors.voornaam}>
              <input
                id="p-voornaam"
                className={cn('input', err('voornaam'))}
                value={f.voornaam ?? ''}
                onChange={(e) => set('voornaam', e.target.value)}
                autoComplete="given-name"
              />
            </Field>
            <Field label="Achternaam" htmlFor="p-achternaam" required error={errors.achternaam}>
              <input
                id="p-achternaam"
                className={cn('input', err('achternaam'))}
                value={f.achternaam ?? ''}
                onChange={(e) => set('achternaam', e.target.value)}
                autoComplete="family-name"
              />
            </Field>
            <Field label="Roepnaam" htmlFor="p-roepnaam">
              <input id="p-roepnaam" className="input" value={f.roepnaam ?? ''} onChange={(e) => set('roepnaam', e.target.value)} />
            </Field>
            <Field label="E-mail" htmlFor="p-email" hint="Hoort bij je Google-account">
              <input id="p-email" type="email" className="input opacity-60" value={profile.email ?? ''} readOnly />
            </Field>
          </FieldGrid>
          {save.bar('naam')}
        </Card>

        <Card title="Identiteit" icon={Lock}>
          <p className="t-muted -mt-1 mb-3">Versleuteld opgeslagen.</p>
          <FieldGrid>
            <Field label="Geboortedatum" htmlFor="p-birth-date">
              <input
                id="p-birth-date"
                type="date"
                lang="nl"
                placeholder="dd-mm-jjjj"
                className={cn('input', err('birth_date'))}
                value={f.birth_date ?? ''}
                onChange={(e) => set('birth_date', e.target.value)}
              />
            </Field>
            <Field label="BSN" htmlFor="bsn" error={errors.bsn}>
              <BsnField value={f.bsn ?? ''} onChange={(v) => set('bsn', v)} error={errors.bsn} />
            </Field>
            <Field label="Geboorteplaats" htmlFor="p-birth-place">
              <input id="p-birth-place" className="input" value={f.birth_place ?? ''} onChange={(e) => set('birth_place', e.target.value)} />
            </Field>
            <Field label="Geboorteland" htmlFor="p-birth-country">
              <input
                id="p-birth-country"
                className="input"
                value={f.birth_country ?? 'Nederland'}
                onChange={(e) => set('birth_country', e.target.value)}
              />
            </Field>
          </FieldGrid>
          {save.bar('identiteit')}
        </Card>

        <Card title="Contact en adres" icon={Lock}>
          <p className="t-muted -mt-1 mb-3">Telefoon en adres worden versleuteld opgeslagen.</p>
          <FieldGrid>
            <Field label="Telefoon" htmlFor="p-phone" error={errors.phone}>
              <input
                id="p-phone"
                type="tel"
                inputMode="tel"
                className={cn('input', err('phone'))}
                value={f.phone ?? ''}
                onChange={(e) => set('phone', e.target.value)}
                autoComplete="tel"
                placeholder="0612345678"
              />
            </Field>
            <div className="hidden sm:block" />
            <Field label="Straat en huisnummer" htmlFor="p-street" wide>
              <input
                id="p-street"
                className="input"
                value={f.address_street ?? ''}
                onChange={(e) => set('address_street', e.target.value)}
                autoComplete="street-address"
              />
            </Field>
            <Field label="Postcode" htmlFor="p-postcode" error={errors.address_postcode}>
              <input
                id="p-postcode"
                className={cn('input', err('address_postcode'))}
                value={f.address_postcode ?? ''}
                onChange={(e) => set('address_postcode', e.target.value)}
                onBlur={(e) => e.target.value && set('address_postcode', normalizeDutchPostcode(e.target.value))}
                autoComplete="postal-code"
                placeholder="1234 AB"
              />
            </Field>
            <Field label="Plaats" htmlFor="p-city">
              <input
                id="p-city"
                className="input"
                value={f.address_city ?? ''}
                onChange={(e) => set('address_city', e.target.value)}
                autoComplete="address-level2"
              />
            </Field>
          </FieldGrid>
          {save.bar('contact')}
        </Card>

        <Card title="Bank">
          <p className="t-muted -mt-1 mb-3">Komt van je laatste loonstrook.</p>
          <FieldGrid>
            <Field label="IBAN" htmlFor="p-iban" error={errors.iban} wide>
              <input
                id="p-iban"
                className={cn('input font-mono uppercase', err('iban'))}
                value={f.iban ?? ''}
                onChange={(e) => set('iban', e.target.value.toUpperCase())}
                placeholder="NL00 BANK 0000 0000 00"
              />
            </Field>
          </FieldGrid>
          {save.bar('bank')}
        </Card>
      </form>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-surface-line/60 pt-4">
        <p className="t-muted">Introductie opnieuw doorlopen. Je gegevens blijven bewaard.</p>
        <button
          type="button"
          onClick={() => {
            if (!confirm('Weet je het zeker? De introductie begint weer bij stap 1. Je gegevens blijven bewaard.')) return;
            resetMut.mutate();
          }}
          className="btn-ghost btn-sm"
          disabled={resetMut.isPending}
        >
          <RotateCcw className={ICON.sm} aria-hidden />
          {resetMut.isPending ? 'Bezig…' : 'Opnieuw starten'}
        </button>
      </div>
    </>
  );
}

// --- Werkgever ----------------------------------------------------------------

function TrigionSection() {
  const { data: profile } = useQuery({ queryKey: ['profile'], queryFn: api.profile.get });
  const { data: nextBump, isError: nextBumpErr } = useQuery({
    queryKey: ['salary', 'next-trede-bump'],
    queryFn: api.salary.nextTredeBump,
    retry: false,
  });
  const [form, setForm] = useState<any | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useProfileSave();

  const loon = nextBumpErr ? (
    <Card>
      <EmptyState icon={Coins} title="Nog geen loongegevens" text="Die verschijnen zodra er een loonstrook of contract is ingelezen." />
    </Card>
  ) : !nextBump ? (
    <div className="grid grid-cols-2 gap-3 sm:gap-4">
      {[0, 1].map((i) => (
        <div key={i} className="card space-y-3">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-7 w-28" />
        </div>
      ))}
    </div>
  ) : (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <Kpi
          icon={Coins}
          label="Uurloon nu"
          value={nextBump.current.uurloon}
          format={formatMoney}
          rows={[
            ['Schaal', String(nextBump.current.schaal)],
            ['Trede', String(nextBump.current.trede)],
            ['Sinds', formatDate(nextBump.current.effective_since)],
          ]}
        />
        {nextBump.at_max_trede ? (
          <Kpi
            icon={TrendingUp}
            label="Volgende periodiek"
            text="Geen"
            tone="muted"
            hint={`Je zit op de hoogste trede (${nextBump.max_trede})`}
          />
        ) : (
          <Kpi
            icon={TrendingUp}
            label="Volgende periodiek"
            value={nextBump.next.expected_uurloon}
            format={formatMoney}
            text={nextBump.next.expected_uurloon == null ? 'Onbekend' : undefined}
            rows={[
              ['Trede', String(nextBump.next.trede)],
              ['Datum', formatDate(nextBump.next_bump_date)],
              ['Over', `${nextBump.days_until} dagen`],
              nextBump.next.delta_eur != null &&
                nextBump.next.delta_pct != null && [
                  'Erbij per uur',
                  <span className="text-money">
                    +{formatMoney(nextBump.next.delta_eur)} ({nextBump.next.delta_pct.toFixed(2).replace('.', ',')}%)
                  </span>,
                ],
            ]}
          />
        )}
      </div>
      {nextBump.source_cao_version && (
        <p className="text-xs text-slate-500">
          Berekend met de loontabel van de huidige cao (tot 27-12-2026) plus de verhoging van 2026.
          {nextBump.next_bump_date > '2026-12-27' && ' Een nieuwe cao kan dit bedrag nog verhogen.'}
        </p>
      )}
    </div>
  );

  if (!profile)
    return (
      <>
        {loon}
        <CardSkeleton rows={4} />
      </>
    );

  const f: any = form ?? profile;
  const set = (k: string, v: any) => setForm({ ...(form ?? profile), [k]: v });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const err: Record<string, string> = {};
    if (!f.personeelsnummer?.trim()) err.personeelsnummer = 'Personeelsnummer is verplicht';
    if (!f.datum_in_dienst?.trim()) err.datum_in_dienst = 'Datum in dienst is verplicht';
    setErrors(err);
    if (Object.keys(err).length > 0) {
      save.fail('Controleer de gemarkeerde velden.');
      return;
    }
    save.clear();
    const payload: Record<string, any> = {
      personeelsnummer: f.personeelsnummer,
      datum_in_dienst: f.datum_in_dienst,
      manager_naam: f.manager_naam ?? null,
      praktijkopleider_naam: f.praktijkopleider_naam ?? null,
      afdeling: f.afdeling ?? null,
      plangebied: f.plangebied ?? null,
      kostenplaats: f.kostenplaats ?? null,
      functiecode: f.functiecode ?? null,
      studentnummer: f.studentnummer ?? null,
      profitcenter: f.profitcenter ?? null,
    };
    save.mut.mutate(payload);
  };

  const textField = (key: string, label: string, mono = false) => (
    <Field label={label} htmlFor={`t-${key}`}>
      <input id={`t-${key}`} className={cn('input', mono && 'font-mono')} value={f[key] ?? ''} onChange={(e) => set(key, e.target.value)} />
    </Field>
  );

  return (
    <>
      {loon}
      <form className="page" onSubmit={submit} noValidate>
        <Card title="Dienstverband">
          <p className="t-muted -mt-1 mb-3">Datum in dienst komt uit je AFAS-contracten en wordt automatisch bijgewerkt.</p>
          <FieldGrid>
            <Field label="Personeelsnummer" htmlFor="t-personeelsnummer" required error={errors.personeelsnummer}>
              <input
                id="t-personeelsnummer"
                className={cn('input font-mono', errors.personeelsnummer && 'border-red-500')}
                value={f.personeelsnummer ?? ''}
                onChange={(e) => set('personeelsnummer', e.target.value)}
              />
            </Field>
            <Field label="Datum in dienst" htmlFor="t-datum-in-dienst" required error={errors.datum_in_dienst}>
              <input
                id="t-datum-in-dienst"
                type="date"
                lang="nl"
                placeholder="dd-mm-jjjj"
                className={cn('input', errors.datum_in_dienst && 'border-red-500')}
                value={f.datum_in_dienst ?? ''}
                onChange={(e) => set('datum_in_dienst', e.target.value)}
              />
            </Field>
            {textField('manager_naam', 'Manager')}
            {textField('praktijkopleider_naam', 'Praktijkopleider')}
          </FieldGrid>
          {save.bar('dienstverband')}
        </Card>

        <Card title="Administratie">
          <p className="t-muted -mt-1 mb-3">De afdeling volgt AFAS (functie en afdeling). De andere velden staan niet in AFAS; die vul je zelf in.</p>
          <FieldGrid>
            {textField('afdeling', 'Afdeling')}
            {textField('plangebied', 'Plangebied')}
            {textField('kostenplaats', 'Kostenplaats')}
            {textField('functiecode', 'Functiecode', true)}
            {textField('studentnummer', 'Studentnummer SVPB', true)}
            {textField('profitcenter', 'Profitcenter', true)}
          </FieldGrid>
          {save.bar('administratie')}
        </Card>
      </form>
    </>
  );
}

// --- Objecten -----------------------------------------------------------------

function ObjectsSection() {
  const qc = useQueryClient();
  const { data: objects = [], isLoading } = useQuery({ queryKey: ['objects'], queryFn: api.objects.list });
  const [editing, setEditing] = useState<null | number | 'new'>(null);
  const [form, setForm] = useState<any>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMut = useMutation({
    mutationFn: (data: any) => api.objects.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['objects'] });
      setEditing(null);
    },
  });
  const updateMut = useMutation({
    mutationFn: ({ id, data }: any) => api.objects.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['objects'] });
      setEditing(null);
    },
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => api.objects.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['objects'] }),
  });

  const saveObject = () => {
    const err: Record<string, string> = {};
    if (!form.naam?.trim()) err.naam = 'Naam is verplicht';
    if (form.postcode && !isValidDutchPostcode(form.postcode)) err.postcode = 'Gebruik het formaat 1234 AB';
    setErrors(err);
    if (Object.keys(err).length > 0) return;
    const payload: any = { ...form };
    if (payload.postcode) payload.postcode = normalizeDutchPostcode(payload.postcode);
    if (editing === 'new') createMut.mutate(payload);
    else updateMut.mutate({ id: editing, data: payload });
  };

  const openNew = () => {
    createMut.reset();
    updateMut.reset();
    setForm({ naam: '', postcode: '', distance_km: null });
    setErrors({});
    setEditing('new');
  };

  const saving = createMut.isPending || updateMut.isPending;

  return (
    <Card
      flush
      title={`Werklocaties${objects.length ? ` (${objects.length})` : ''}`}
      icon={MapPin}
      action={
        <button type="button" onClick={openNew} className="btn-secondary">
          <Plus className={ICON.sm} aria-hidden /> Toevoegen
        </button>
      }
    >
      <p className="t-muted -mt-1 px-4 pb-3 sm:px-5">De afstand bepaalt je reiskostenvergoeding.</p>

      {isLoading ? (
        <div className="space-y-2 border-t border-surface-line px-4 py-4 sm:px-5">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      ) : objects.length === 0 ? (
        <div className="border-t border-surface-line">
          <EmptyState
            icon={MapPin}
            title="Nog geen werklocaties"
            text="Ze komen vanzelf binnen met je urencontroles. Je kunt er ook zelf een toevoegen."
          />
        </div>
      ) : (
        <div className="border-t border-surface-line">
          <div className="hidden grid-cols-[minmax(0,1fr)_6rem_5.5rem] items-center gap-3 px-4 py-2 sm:grid sm:px-5">
            <span className="t-label">Naam</span>
            <span className="t-label text-right">Afstand</span>
            <span className="sr-only">Acties</span>
          </div>
          <div className="list border-t border-surface-line/60">
            {objects.map((o, i) => (
              <div
                key={o.id}
                className="row enter grid grid-cols-[minmax(0,1fr)_auto_auto] sm:grid-cols-[minmax(0,1fr)_6rem_5.5rem]"
                style={{ '--i': i } as React.CSSProperties}
              >
                <div className="min-w-0">
                  <div className="truncate text-sm text-slate-100">{o.naam}</div>
                  <div className="truncate text-xs text-slate-400">
                    {[o.postcode, o.city, o.objectnummer].filter(Boolean).join(' · ') || 'Geen adres'}
                  </div>
                </div>
                <div className={cn('t-num text-right text-sm', o.distance_km != null ? 'text-slate-100' : 'text-slate-500')}>
                  {o.distance_km != null ? `${String(o.distance_km).replace('.', ',')} km` : 'Onbekend'}
                </div>
                <div className="-mr-2 flex justify-end">
                  <IconButton
                    icon={Pencil}
                    label={`${o.naam} bewerken`}
                    onClick={() => {
                      createMut.reset();
                      updateMut.reset();
                      setForm({ ...o });
                      setErrors({});
                      setEditing(o.id);
                    }}
                  />
                  <IconButton
                    icon={Trash2}
                    label={`${o.naam} verwijderen`}
                    className="hover:text-red-300"
                    onClick={() => {
                      if (confirm(`${o.naam} verwijderen? Diensten op deze locatie blijven bestaan, maar zonder locatie.`)) deleteMut.mutate(o.id);
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Sheet
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Nieuwe werklocatie' : 'Werklocatie bewerken'}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setEditing(null)} className="btn-ghost">
              Annuleren
            </button>
            <button type="button" onClick={saveObject} className="btn-primary" disabled={saving}>
              {saving ? 'Opslaan…' : 'Opslaan'}
            </button>
          </div>
        }
      >
        <div className="space-y-3">
          <FieldGrid>
            <Field label="Naam" htmlFor="o-naam" required error={errors.naam} wide>
              <input
                id="o-naam"
                className={cn('input', errors.naam && 'border-red-500')}
                value={form.naam ?? ''}
                onChange={(e) => setForm({ ...form, naam: e.target.value })}
                placeholder="COA Fletcher VA"
              />
            </Field>
            <Field label="Objectnummer" htmlFor="o-nummer">
              <input
                id="o-nummer"
                className="input font-mono"
                value={form.objectnummer ?? ''}
                onChange={(e) => setForm({ ...form, objectnummer: e.target.value })}
                placeholder="150-1234"
              />
            </Field>
            <Field label="Afstand enkele reis (km)" htmlFor="o-afstand" hint="Leeg laten om hem te laten berekenen">
              <input
                id="o-afstand"
                type="number"
                step="0.1"
                min="0"
                inputMode="decimal"
                className="input"
                value={form.distance_km ?? ''}
                onChange={(e) => setForm({ ...form, distance_km: e.target.value === '' ? null : parseFloat(e.target.value) })}
                placeholder="16,4"
              />
            </Field>
            <Field label="Adres" htmlFor="o-adres" wide>
              <input
                id="o-adres"
                className="input"
                value={form.address_street ?? ''}
                onChange={(e) => setForm({ ...form, address_street: e.target.value })}
                placeholder="Laan van Meerdervoort 108"
              />
            </Field>
            <Field label="Postcode" htmlFor="o-postcode" error={errors.postcode}>
              <input
                id="o-postcode"
                className={cn('input', errors.postcode && 'border-red-500')}
                value={form.postcode ?? ''}
                onChange={(e) => setForm({ ...form, postcode: e.target.value })}
                onBlur={(e) => e.target.value && setForm({ ...form, postcode: normalizeDutchPostcode(e.target.value) })}
                placeholder="1234 AB"
              />
            </Field>
            <Field label="Plaats" htmlFor="o-plaats">
              <input
                id="o-plaats"
                className="input"
                value={form.city ?? ''}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                placeholder="'s-Gravenhage"
              />
            </Field>
          </FieldGrid>
          {(createMut.isError || updateMut.isError) && (
            <Notice tone="err">{((createMut.error ?? updateMut.error) as Error | undefined)?.message ?? 'Opslaan mislukt'}</Notice>
          )}
        </div>
      </Sheet>
    </Card>
  );
}

// --- Rooster ------------------------------------------------------------------

const ICS_STATUS_LABEL: Record<string, string> = {
  ok: 'Werkt',
  no_events: 'Geen diensten gevonden',
};

const icsStatusLabel = (st: string) =>
  ICS_STATUS_LABEL[st] ??
  (/^http_5/.test(st)
    ? 'Storing bij RostarCAS, de app probeert het elke 8 uur opnieuw'
    : /^http_(401|403|404|410)$/.test(st)
      ? 'Link werkt niet meer, plak een nieuwe link uit RostarCAS'
      : st);

function RoosterSyncSection() {
  const qc = useQueryClient();
  const { data: ics, isLoading } = useQuery({ queryKey: ['rooster-ics-status'], queryFn: api.rooster.icsStatus });
  const [url, setUrl] = useState('');

  const saveMut = useMutation({
    mutationFn: (u: string) => api.rooster.setIcsUrl(u),
    onSuccess: () => {
      setUrl('');
      qc.invalidateQueries({ queryKey: ['rooster-ics-status'] });
    },
  });
  const syncMut = useMutation({
    mutationFn: () => api.rooster.icsSync(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rooster-ics-status'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['shifts'] });
    },
  });

  if (isLoading) return <CardSkeleton rows={3} />;

  const statusCls =
    ics?.last_status === 'ok'
      ? 'status-ok'
      : ics?.last_status == null
        ? 'status-muted'
        : ics?.last_status === 'no_events'
          ? 'status-warn'
          : 'status-err';

  return (
    <>
      <Card
        title="Roosterlink"
        icon={CalendarDays}
        action={
          ics?.configured && (
            <button type="button" onClick={() => syncMut.mutate()} className="btn-primary" disabled={syncMut.isPending}>
              {syncMut.isPending ? 'Ophalen…' : 'Rooster ophalen'}
            </button>
          )
        }
      >
        <div className="space-y-4">
          <p className="t-muted">Je rooster uit RostarCAS wordt elke 8 uur bijgewerkt.</p>

          {ics?.configured && (
            <InfoList>
              <InfoRow label="Status">
                <span className={statusCls}>
                  {ics.last_status == null ? 'Nog niet opgehaald' : icsStatusLabel(ics.last_status)}
                </span>
                {ics.last_event_count != null && ics.last_status === 'ok' && <span className="t-num text-slate-400"> ({ics.last_event_count} diensten)</span>}
              </InfoRow>
              {ics.last_fetched_at && (
                <InfoRow label="Laatst opgehaald">
                  <span className="t-num">{formatDate(ics.last_fetched_at, 'dd-MM-yyyy HH:mm')}</span>
                </InfoRow>
              )}
              {ics.last_sync && (
                <InfoRow label="Laatste verwerking">
                  <span className="t-num">
                    {ics.last_sync.shifts_created} nieuw van {ics.last_sync.shifts_seen}
                  </span>
                </InfoRow>
              )}
              <InfoRow label="Link">
                <span className="break-all font-mono text-xs text-slate-300">{ics.ics_url_masked}</span>
              </InfoRow>
            </InfoList>
          )}

          <Field label={ics?.configured ? 'Nieuwe link' : 'Agendalink'} htmlFor="ics-url">
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                id="ics-url"
                type="text"
                className="input font-mono"
                placeholder="https://facilicomweb.rostarcas.com/…/schedule.ics"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
              <button
                type="button"
                onClick={() => saveMut.mutate(url.trim())}
                className="btn-secondary shrink-0"
                disabled={!url.trim() || saveMut.isPending}
              >
                {saveMut.isPending ? 'Opslaan…' : 'Opslaan'}
              </button>
            </div>
          </Field>

          {saveMut.isSuccess && <Notice tone="ok">Roosterlink opgeslagen.</Notice>}
          {saveMut.isError && <Notice tone="err">{(saveMut.error as Error).message}</Notice>}
          {syncMut.isSuccess && syncMut.data && (
            <Notice tone="ok">
              {syncMut.data.created ?? 0} nieuw, {syncMut.data.updated ?? 0} bijgewerkt, {syncMut.data.unchanged ?? 0} ongewijzigd
              {(syncMut.data.skipped ?? 0) > 0 && `, ${syncMut.data.skipped} overgeslagen`}.
            </Notice>
          )}
          {syncMut.isError && <Notice tone="err">{(syncMut.error as Error).message}</Notice>}
        </div>

        <Disclosure title="Waar vind ik de link?" className="-mx-4 -mb-4 mt-3 border-t border-surface-line/60 sm:-mx-5 sm:-mb-5">
          <ol className="list-decimal space-y-1 pl-5 text-sm text-slate-400">
            <li>Open RostarCAS en ga naar Account, Instellingen, Agendakoppeling.</li>
            <li>Kopieer de link voor Google en andere agenda's (eindigt op schedule.ics).</li>
            <li>Plak hem hierboven en kies Opslaan.</li>
          </ol>
        </Disclosure>
      </Card>

      <div className="card-flush">
        <Disclosure title="Inloggen met cookies" meta="Alternatief voor de roosterlink">
          <LegacyCookieSync />
        </Disclosure>
      </div>
    </>
  );
}

const COOKIE_STATUS_LABEL: Record<string, string> = {
  ok: 'Werkt',
  needs_reauth: 'Opnieuw inloggen nodig',
};

function LegacyCookieSync() {
  const qc = useQueryClient();
  const { data: status } = useQuery({ queryKey: ['rooster-status'], queryFn: api.rooster.status });
  const [raw, setRaw] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [preview, setPreview] = useState<Array<{ name: string; value: string; expires?: number; domain?: string }> | null>(null);

  const saveMut = useMutation({
    mutationFn: (data: any) => api.rooster.setCookies(data),
    onSuccess: () => {
      setRaw('');
      setPreview(null);
      qc.invalidateQueries({ queryKey: ['rooster-status'] });
    },
  });

  const parseAndPreview = () => {
    setParseError(null);
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) throw new Error('Verwacht een JSON-lijst met cookies');
      const filtered = parsed
        .filter((c: any) => typeof c?.name === 'string' && typeof c?.value === 'string')
        .map((c: any) => ({
          name: c.name,
          value: c.value,
          domain: c.domain,
          path: c.path,
          expires: typeof c.expirationDate === 'number' ? c.expirationDate : c.expires,
          httpOnly: c.httpOnly,
          secure: c.secure,
          sameSite: typeof c.sameSite === 'string' ? c.sameSite : undefined,
        }));
      const roster = filtered.filter((c: any) => !c.domain || /rostarcas\.com$/.test(c.domain) || c.domain.endsWith('rostarcas.com'));
      if (roster.length === 0) throw new Error('Geen cookies van RostarCAS gevonden');
      setPreview(roster);
    } catch (e: any) {
      setParseError(e.message);
      setPreview(null);
    }
  };

  const submit = () => {
    if (!preview) return;
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : undefined;
    const longest = preview.reduce<number | undefined>((acc, c) => (c.expires && (!acc || c.expires > acc) ? c.expires : acc), undefined);
    saveMut.mutate({
      cookies: preview,
      user_agent: ua,
      expires_at: longest ? new Date(longest * 1000).toISOString() : undefined,
    });
  };

  const cred = status?.credentials;
  const lastSync = status?.last_sync;
  const needsReauth = cred?.last_status === 'needs_reauth';

  return (
    <div className="space-y-4">
      <p className="t-muted">Ongeveer eens per 7 maanden opnieuw nodig.</p>

      {!cred && <p className="t-muted">Er zijn nog geen cookies opgeslagen.</p>}
      {cred && (
        <InfoList>
          <InfoRow label="Opgeslagen op">
            <span className="t-num">{formatDate(cred.captured_at, 'dd-MM-yyyy HH:mm')}</span>
          </InfoRow>
          {cred.expires_at &&
            (() => {
              const days = Math.floor((new Date(cred.expires_at).getTime() - Date.now()) / 86_400_000);
              const color = days < 7 ? 'text-red-300' : days < 30 ? 'text-amber-200' : 'text-slate-100';
              return (
                <InfoRow label="Verloopt">
                  <span className={cn('t-num', color)}>
                    {formatDate(cred.expires_at, 'dd-MM-yyyy')} ({days > 0 ? `nog ${days} dagen` : days === 0 ? 'vandaag' : `${-days} dagen geleden`})
                  </span>
                </InfoRow>
              );
            })()}
          {cred.last_used_at && (
            <InfoRow label="Laatst gebruikt">
              <span className="t-num">{formatDate(cred.last_used_at, 'dd-MM-yyyy HH:mm')}</span>
            </InfoRow>
          )}
          {cred.last_status && (
            <InfoRow label="Status">
              <span className={cred.last_status === 'ok' ? 'status-ok' : needsReauth ? 'status-err' : 'status-warn'}>
                {COOKIE_STATUS_LABEL[cred.last_status] ?? cred.last_status}
              </span>
            </InfoRow>
          )}
          {lastSync && (
            <InfoRow label="Laatste verwerking">
              <span className="t-num">
                {formatDate(lastSync.synced_at, 'dd-MM-yyyy HH:mm')}, {lastSync.shifts_created} nieuw van {lastSync.shifts_seen}
              </span>
            </InfoRow>
          )}
        </InfoList>
      )}
      {needsReauth && <Notice tone="err">RostarCAS accepteert de cookies niet meer. Log opnieuw in en plak nieuwe cookies hieronder.</Notice>}

      <ol className="list-decimal space-y-1 pl-5 text-sm text-slate-400">
        <li>Log in op RostarCAS (facilicomweb.rostarcas.com).</li>
        <li>Exporteer je cookies als JSON met de extensie Cookie Editor.</li>
        <li>Plak ze hieronder en kies Controleren.</li>
      </ol>

      <Field label="Cookies" htmlFor="cookie-json">
        <textarea
          id="cookie-json"
          className="input min-h-[180px] font-mono text-xs"
          placeholder='[ { "name": "refreshtoken", "value": "…", "domain": "facilicomweb.rostarcas.com" } ]'
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
        />
      </Field>
      {parseError && <Notice tone="err">{parseError}</Notice>}
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" onClick={parseAndPreview} className="btn-secondary" disabled={!raw.trim()}>
          Controleren
        </button>
        {preview && (
          <button type="button" onClick={submit} className="btn-primary" disabled={saveMut.isPending}>
            {saveMut.isPending ? 'Opslaan…' : `Opslaan (${preview.length} cookies)`}
          </button>
        )}
      </div>

      {preview && (
        <div>
          <div className="t-label mb-1">{preview.length} cookies herkend</div>
          <ul className="divide-y divide-surface-line/60 text-sm">
            {preview.map((c) => (
              <li key={c.name} className="flex justify-between gap-4 py-1.5">
                <span className="truncate font-mono text-xs text-slate-200">{c.name}</span>
                <span className="t-num whitespace-nowrap text-slate-400">
                  {c.expires ? formatDate(new Date(c.expires * 1000).toISOString()) : 'Sessie'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {saveMut.isSuccess && <Notice tone="ok">Cookies opgeslagen. Je rooster wordt elke 8 uur bijgewerkt.</Notice>}
      {saveMut.isError && <Notice tone="err">{(saveMut.error as Error).message}</Notice>}
    </div>
  );
}

// --- Agenda -------------------------------------------------------------------

const CALENDAR_TYPES: Array<{
  key: keyof Pick<CalendarSettings, 'sync_work' | 'sync_bhv' | 'sync_opleiding' | 'sync_vakantie' | 'sync_ziekte' | 'sync_buitengewoon'>;
  label: string;
  description: string;
}> = [
  { key: 'sync_work', label: 'Reguliere diensten', description: 'Object en tijden in de titel' },
  { key: 'sync_bhv', label: 'BHV-diensten', description: 'Met begin- en eindtijd' },
  { key: 'sync_opleiding', label: 'Opleiding en training', description: 'Met begin- en eindtijd' },
  { key: 'sync_vakantie', label: 'Vakantie', description: 'Als hele dag' },
  { key: 'sync_ziekte', label: 'Ziekte', description: 'Als hele dag, staat standaard uit' },
  { key: 'sync_buitengewoon', label: 'Buitengewoon verlof', description: 'Als hele dag' },
];

const QUEUE_ACTION: Record<string, string> = {
  create: 'toevoegen',
  update: 'bijwerken',
  delete: 'verwijderen',
};

function FailedList({ title, items }: { title: string; items: Array<{ key: string | number; text: string }> }) {
  return (
    <Disclosure title={title} className="-mx-4 sm:-mx-5">
      <ul className="space-y-1 text-sm text-red-300">
        {items.slice(0, 10).map((f) => (
          <li key={f.key}>{f.text}</li>
        ))}
      </ul>
    </Disclosure>
  );
}

function CalendarSection() {
  const qc = useQueryClient();
  const { data: status, isLoading } = useQuery({ queryKey: ['calendar-status'], queryFn: api.calendar.status });
  const { data: settings } = useQuery({
    queryKey: ['calendar-settings'],
    queryFn: api.calendar.settings,
    enabled: !!status?.connected,
  });
  const { data: queue } = useQuery({
    queryKey: ['calendar-queue'],
    queryFn: api.calendar.queue,
    enabled: !!status?.connected,
    refetchInterval: 10_000,
  });
  const disconnect = useMutation({
    mutationFn: api.calendar.disconnect,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['calendar-status'] }),
  });
  const pushAll = useMutation({
    mutationFn: (force: boolean) => api.calendar.pushAll({ force }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['calendar-status'] });
      qc.invalidateQueries({ queryKey: ['calendar-queue'] });
    },
    onError: () => qc.invalidateQueries({ queryKey: ['calendar-status'] }),
  });
  const reconcile = useMutation({
    // Loop tot more=false: bij veel events kan de opschoning meerdere budget-batches kosten.
    mutationFn: async () => {
      let last = await api.calendar.reconcile();
      let agg = { ...last };
      let guard = 0;
      while (last.ok && last.more && guard < 20) {
        guard++;
        last = await api.calendar.reconcile();
        agg = {
          ...last,
          duplicates_removed: agg.duplicates_removed + last.duplicates_removed,
          orphans_removed: agg.orphans_removed + last.orphans_removed,
          created: agg.created + last.created,
          updated: agg.updated + last.updated,
          enqueued: last.enqueued,
          failed: [...agg.failed, ...last.failed],
        };
      }
      return agg;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['calendar-status'] });
      qc.invalidateQueries({ queryKey: ['calendar-queue'] });
    },
    onError: () => qc.invalidateQueries({ queryKey: ['calendar-status'] }),
  });
  const updateSettings = useMutation({
    mutationFn: (data: Partial<CalendarSettings>) => api.calendar.updateSettings(data),
    onSuccess: (data) => qc.setQueryData(['calendar-settings'], data),
  });
  const runQueue = useMutation({
    mutationFn: api.calendar.runQueue,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['calendar-queue'] });
      qc.invalidateQueries({ queryKey: ['calendar-status'] });
    },
  });
  const clearDead = useMutation({
    mutationFn: api.calendar.clearDead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['calendar-queue'] }),
  });

  const connected = !!status?.connected;
  const [reminderInput, setReminderInput] = useState<string>('');
  const effectiveReminder = reminderInput !== '' ? reminderInput : (settings?.reminder_minutes?.toString() ?? '60');

  if (isLoading) return <CardSkeleton rows={3} />;

  if (!connected) {
    return (
      <Card>
        <EmptyState
          icon={CalendarDays}
          title="Google Agenda is niet gekoppeld"
          text="Koppel hem om je diensten automatisch in een aparte agenda te zetten."
          action={
            <a href={api.calendar.connectUrl()} className="btn-primary">
              Google Agenda koppelen
            </a>
          }
        />
      </Card>
    );
  }

  return (
    <>
      <Card title="Google Agenda" icon={CalendarDays} action={<span className="status-ok">Gekoppeld</span>}>
        <div className="space-y-4">
          <InfoList>
            <InfoRow label="Agenda">{status!.calendar_name ?? 'Werk - Diensten'}</InfoRow>
            <InfoRow label="Gekoppeld sinds">
              <span className="t-num">{formatDate(status!.connected_at!, 'dd-MM-yyyy HH:mm')}</span>
            </InfoRow>
            {status!.last_sync_at && (
              <InfoRow label="Laatst bijgewerkt">
                <span className="t-num">{formatDate(status!.last_sync_at, 'dd-MM-yyyy HH:mm')}</span>
                {status!.last_sync_status && <span className="text-slate-400"> ({status!.last_sync_status})</span>}
              </InfoRow>
            )}
            <InfoRow label="Diensten in agenda">
              <span className="t-num">{status!.events_mapped ?? 0}</span>
            </InfoRow>
          </InfoList>

          <div className="flex flex-wrap gap-2">
            <button onClick={() => reconcile.mutate()} disabled={reconcile.isPending || pushAll.isPending} className="btn-primary">
              {reconcile.isPending ? 'Synchroniseren…' : 'Agenda synchroniseren'}
            </button>
            <button onClick={() => pushAll.mutate(false)} disabled={pushAll.isPending || reconcile.isPending} className="btn-secondary">
              {pushAll.isPending ? 'Toevoegen…' : 'Alleen nieuwe toevoegen'}
            </button>
            <button onClick={() => disconnect.mutate()} disabled={disconnect.isPending} className="btn-ghost text-red-300 hover:text-red-200 sm:ml-auto">
              Ontkoppelen
            </button>
          </div>
          <p className="t-muted">Synchroniseren ruimt dubbele afspraken op: elke dienst staat er precies één keer in.</p>

          {reconcile.data && (
            <div className="space-y-1">
              <Notice tone="ok">
                {reconcile.data.duplicates_removed} dubbele en {reconcile.data.orphans_removed} losse afspraken opgeruimd
                {reconcile.data.enqueued > 0 && `, ${reconcile.data.enqueued} diensten worden nog toegevoegd`}.
              </Notice>
              {reconcile.data.failed.length > 0 && (
                <FailedList
                  title={`${reconcile.data.failed.length} mislukt`}
                  items={reconcile.data.failed.map((f, i) => ({ key: i, text: `${String(f.id)}: ${f.error}` }))}
                />
              )}
            </div>
          )}
          {pushAll.data && (
            <div className="space-y-1">
              <Notice tone="ok">
                {pushAll.data.created} nieuw, {pushAll.data.updated} bijgewerkt, {pushAll.data.skipped ?? 0} overgeslagen.
              </Notice>
              {pushAll.data.failed.length > 0 && (
                <FailedList
                  title={`${pushAll.data.failed.length} mislukt, worden later opnieuw geprobeerd`}
                  items={pushAll.data.failed.map((f) => ({ key: f.id, text: `Dienst ${f.id}: ${f.error}` }))}
                />
              )}
            </div>
          )}
          {(reconcile.isError || pushAll.isError) && (
            <Notice tone="err">Synchroniseren mislukt: {((reconcile.error || pushAll.error) as Error)?.message ?? 'onbekende fout'}</Notice>
          )}
        </div>
      </Card>

      {settings && (
        <Card title="Wat komt in je agenda">
          <p className="t-muted -mt-1 mb-3">Uitzetten haalt die afspraken weg bij de volgende synchronisatie.</p>
          <div className="list -mx-4 border-y border-surface-line/60 sm:-mx-5">
            {[
              ...CALENDAR_TYPES.map((t) => ({
                id: t.key,
                label: t.label,
                description: t.description,
                checked: settings[t.key],
                onChange: (v: boolean) => updateSettings.mutate({ [t.key]: v } as Partial<CalendarSettings>),
              })),
            ].map((t) => (
              <label key={t.id} className="row cursor-pointer">
                <input
                  type="checkbox"
                  checked={t.checked}
                  onChange={(e) => t.onChange(e.target.checked)}
                  className="h-4 w-4 shrink-0 accent-brand-400"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-100">{t.label}</div>
                  <div className="text-xs text-slate-400">{t.description}</div>
                </div>
              </label>
            ))}
          </div>

          <div className="pt-4">
            <Field label="Bedrag in de titel" htmlFor="title-amount" hint="Bijvoorbeeld: COA Fletcher 23:00-07:00 €139 netto. Alle bedragen staan ook in de beschrijving.">
              <div id="title-amount" role="radiogroup" className="inline-flex rounded-lg border border-surface-line p-0.5">
                {(['geen', 'bruto', 'netto'] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    role="radio"
                    aria-checked={settings.title_amount === v}
                    onClick={() => updateSettings.mutate({ title_amount: v })}
                    className={cn(
                      'rounded-md px-3 py-1.5 text-sm transition-colors duration-fast ease-calm',
                      settings.title_amount === v ? 'bg-slate-100 text-slate-900' : 'text-slate-300 hover:text-slate-100'
                    )}
                  >
                    {v === 'geen' ? 'Geen' : v === 'bruto' ? 'Bruto' : 'Netto'}
                  </button>
                ))}
              </div>
            </Field>
          </div>

          <div className="pt-4">
            <Field label="Herinnering vooraf (minuten)" htmlFor="reminder-minutes" hint="0 betekent geen herinnering.">
              <div className="flex flex-wrap items-center gap-2">
                <input
                  id="reminder-minutes"
                  type="number"
                  min={0}
                  max={40320}
                  step={5}
                  inputMode="numeric"
                  className="input w-32"
                  value={effectiveReminder}
                  onChange={(e) => setReminderInput(e.target.value)}
                />
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={updateSettings.isPending || Number(effectiveReminder) === settings.reminder_minutes || effectiveReminder === ''}
                  onClick={() => {
                    const n = Number(effectiveReminder);
                    if (!Number.isFinite(n) || n < 0) return;
                    updateSettings.mutate({ reminder_minutes: n });
                    setReminderInput('');
                  }}
                >
                  {updateSettings.isPending ? 'Opslaan…' : 'Opslaan'}
                </button>
              </div>
            </Field>
          </div>
        </Card>
      )}

      {queue && (
        <Card
          title="Wachtrij"
          action={
            <div className="flex gap-2">
              {queue.counts.dead > 0 && (
                <button type="button" onClick={() => clearDead.mutate()} disabled={clearDead.isPending} className="btn-ghost text-red-300 hover:text-red-200">
                  Opgegeven wissen
                </button>
              )}
              <button type="button" onClick={() => runQueue.mutate()} disabled={runQueue.isPending} className="btn-secondary">
                {runQueue.isPending ? 'Verwerken…' : 'Nu verwerken'}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <dl className="grid grid-cols-3 gap-3">
              <QueueStat label="Wachtend" value={queue.counts.pending ?? 0} className={queue.counts.pending ? 'text-amber-200' : 'text-slate-300'} />
              <QueueStat label="Verwerkt" value={queue.counts.done ?? 0} className="text-money" />
              <QueueStat label="Opgegeven" value={queue.counts.dead ?? 0} className={queue.counts.dead ? 'text-red-300' : 'text-slate-300'} />
            </dl>
            {runQueue.data && (
              <Notice tone="ok">
                {runQueue.data.succeeded} verwerkt, {runQueue.data.retried} opnieuw geprobeerd, {runQueue.data.dead} opgegeven.
              </Notice>
            )}
            {queue.pending.length > 0 && (
              <div className="list -mx-4 border-t border-surface-line/60 sm:-mx-5">
                {queue.pending.slice(0, 8).map((q) => (
                  <div key={q.id} className="row text-sm">
                    <span className="t-num shrink-0 text-slate-200">Dienst {q.shift_id}</span>
                    <span className="shrink-0 text-slate-400">
                      {QUEUE_ACTION[q.action] ?? q.action}, {q.attempts}×
                    </span>
                    <span className="min-w-0 flex-1 truncate text-right text-xs text-amber-200">{q.last_error ?? 'Wacht op verwerking'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}
    </>
  );
}

function QueueStat({ label, value, className }: { label: string; value: number; className?: string }) {
  return (
    <div className="min-w-0">
      <dt className="t-label">{label}</dt>
      <dd className={cn('t-num mt-1 text-base font-semibold', className)}>{value}</dd>
    </div>
  );
}

// --- Meldingen (momenteel niet in de tabs) ---------------------------------------

const PERMISSION_LABEL: Record<string, string> = {
  granted: 'Toegestaan',
  denied: 'Geweigerd',
  default: 'Nog niet gevraagd',
};

function NotificationsSection() {
  const [permission, setPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testSent, setTestSent] = useState(false);

  const checkSub = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setSubscribed(false);
      return;
    }
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      setSubscribed(!!sub);
    } catch {
      setSubscribed(false);
    }
  };

  useState(() => {
    checkSub();
    return 0;
  });

  const enable = async () => {
    setError(null);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') throw new Error('Permissie geweigerd');
      const reg = await navigator.serviceWorker.register('/sw.js');
      const { publicKey } = await api.push.vapidPublicKey();
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey).buffer as ArrayBuffer,
      });
      await api.push.subscribe(sub.toJSON() as PushSubscriptionJSON);
      setSubscribed(true);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const test = async () => {
    setTestSent(false);
    try {
      const res = await api.push.test();
      setTestSent(res.ok && res.sent > 0);
    } catch (e: any) {
      setError(e.message);
    }
  };

  return (
    <Card title="Meldingen">
      <div className="space-y-4">
        <p className="t-muted">Een melding een uur voor elke dienst en bij belangrijke zaken, zoals te weinig rust.</p>
        <InfoList>
          <InfoRow label="Toestemming browser">
            <span className={permission === 'granted' ? 'status-ok' : permission === 'denied' ? 'status-err' : 'status-warn'}>
              {PERMISSION_LABEL[permission] ?? permission}
            </span>
          </InfoRow>
          <InfoRow label="Aangemeld">
            <span className={subscribed ? 'status-ok' : 'status-warn'}>{subscribed === null ? 'Controleren…' : subscribed ? 'Ja' : 'Nee'}</span>
          </InfoRow>
        </InfoList>
        <div className="flex flex-wrap gap-2">
          {!subscribed && (
            <button onClick={enable} className="btn-primary">
              Meldingen aanzetten
            </button>
          )}
          {subscribed && (
            <button onClick={test} className="btn-secondary">
              Testmelding sturen
            </button>
          )}
        </div>
        {testSent && <Notice tone="ok">Testmelding verstuurd.</Notice>}
        {error && <Notice tone="err">{error}</Notice>}
        <p className="text-xs text-slate-500">Op iPhone werken meldingen alleen als de app op je beginscherm staat.</p>
      </div>
    </Card>
  );
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const out = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) out[i] = rawData.charCodeAt(i);
  return out;
}
